import React from 'react';
import { DistanceType } from '../../../enums/DistanceType';
import { DataSource } from '../../../enums/DataSource';
import { RecipeType } from '../../../enums/RecipeType';
import { strings } from '../../../resources/strings';
import { recipeResources } from '../../../resources/recipeResources';
import { Recipe } from '../../../models/Recipe';
import { Table } from '../../../common-components/Table/Table';
import { Button } from '../../../common-components/Button/Button';
import { 
    CardContainer, CardContent, Buttons, 
    CardTabulatedData, CardSimpleData, CardApprovalData, CardData,
    TableHeaderRecipe, TableBodyRecipe, TableRowRecipe,
    TableColumnHeaderRecipe, TableColumnBodyRecipe
} from './styles';

interface ICardRecipeProps {
    recipe: Recipe;
    readonly?: boolean;
    orderDataSource?: DataSource;
    editRecipe: () => void;
    deleteRecipe: () => void;
}

export const CardRecipe: React.FC<ICardRecipeProps> = ({
    recipe,
    readonly,
    orderDataSource,
    editRecipe,
    deleteRecipe
}) => {

    const farDiagnostic = recipe.diagnosticsInformations.find(xdi => xdi.distanceType === DistanceType.Far);
    const nearDiagnostic = recipe.diagnosticsInformations.find(xdi => xdi.distanceType === DistanceType.Near);

    let originRecipeCreation = '';
    if (farDiagnostic?.createByName || nearDiagnostic?.createByName) {
        originRecipeCreation = strings.RecipeRegisteredBy.replace('{user}', farDiagnostic?.createByName || nearDiagnostic?.createByName || '');
    } else if (orderDataSource === DataSource.Migration) {
        originRecipeCreation = strings.RecipeSourcedFromTheOldSislog;
    } else {
        originRecipeCreation = strings.RecipeRegisteredInCustomerArea;
    }

    let originRecipeChange = '';
    if (farDiagnostic?.updateByName || nearDiagnostic?.updateByName) {
        originRecipeChange = strings.RecipeUpdatedBy.replace('{user}', farDiagnostic?.updateByName || nearDiagnostic?.updateByName || '');
    }

    const buttons = readonly ? null : (
        <Buttons>
            <Button text={strings.Edit} type='secondary' click={editRecipe} />
            <Button text={strings.Delete} type='secondary' click={deleteRecipe} />
        </Buttons>
    );

    const multifocal = recipe?.recipeType ? +recipe.recipeType === +RecipeType.Bifocal : false;

    const multifocalInfo = multifocal ? (
        <CardTabulatedData>
            <Table bordered={true}>
                <TableHeaderRecipe>
                    <TableRowRecipe>
                        <TableColumnHeaderRecipe width='12%'>{strings.Addition}</TableColumnHeaderRecipe>
                        <TableColumnHeaderRecipe width='12%'>{strings.Bridge}</TableColumnHeaderRecipe>
                        <TableColumnHeaderRecipe width='12%'>{strings.Rim}</TableColumnHeaderRecipe>
                        <TableColumnHeaderRecipe width='12%'>{strings.Vertical}</TableColumnHeaderRecipe>
                        <TableColumnHeaderRecipe width='14%'>{strings.AbbreviationForLargestDiagonal}</TableColumnHeaderRecipe>
                    </TableRowRecipe>
                </TableHeaderRecipe>
                <TableBodyRecipe>
                    <TableRowRecipe>
                        <TableColumnBodyRecipe width='12%'>{recipe.multifocalInfo?.addField}</TableColumnBodyRecipe>
                        <TableColumnBodyRecipe width='12%'>{recipe.multifocalInfo?.actualBridge}</TableColumnBodyRecipe>
                        <TableColumnBodyRecipe width='12%'>{recipe.multifocalInfo?.actualEyeWidth}</TableColumnBodyRecipe>
                        <TableColumnBodyRecipe width='12%'>{recipe.multifocalInfo?.actualEyeHeight}</TableColumnBodyRecipe>
                        <TableColumnBodyRecipe width='14%'>{recipe.multifocalInfo?.actualLargestDiagonal}</TableColumnBodyRecipe>
                    </TableRowRecipe>
                </TableBodyRecipe>
            </Table>
        </CardTabulatedData>
    ) : null;

    return (
        <CardContainer>
            <CardContent>
                <CardTabulatedData>
                    <Table bordered={true}>
                        <TableHeaderRecipe>
                            <TableRowRecipe>
                                <TableColumnHeaderRecipe width='120px'>{strings.Type}</TableColumnHeaderRecipe>
                                <TableColumnHeaderRecipe>{`${strings.Name}/${strings.Description}`}</TableColumnHeaderRecipe>
                                <TableColumnHeaderRecipe width='100px' textAlign='center'>{strings.File}</TableColumnHeaderRecipe>
                            </TableRowRecipe>
                        </TableHeaderRecipe>
                        <TableBodyRecipe>
                            <TableRowRecipe>
                                <TableColumnBodyRecipe width='120px'>{recipeResources.RecipeTypeOptions.find(rt => rt.Type === recipe.recipeType)?.Description || recipeResources.SingleVisionRecipeTypeDescription}</TableColumnBodyRecipe>
                                <TableColumnBodyRecipe>{recipe.customerName}</TableColumnBodyRecipe>
                                <TableColumnBodyRecipe width='100px' textAlign='center'>{recipe.pathToFile ? <a target='_blank' rel='noreferrer' href={recipe.pathToFile}>{strings.Recipe}</a> : ''}</TableColumnBodyRecipe>
                            </TableRowRecipe>
                        </TableBodyRecipe>
                    </Table>
                </CardTabulatedData>

                <CardTabulatedData>
                    <Table bordered={true}>
                        <TableHeaderRecipe>
                            <TableRowRecipe>
                                <TableColumnHeaderRecipe width='70px'>{strings.For}</TableColumnHeaderRecipe>
                                <TableColumnHeaderRecipe width='70px'>{strings.Eye}</TableColumnHeaderRecipe>
                                <TableColumnHeaderRecipe width='16%'>{strings.Spherical}</TableColumnHeaderRecipe>
                                <TableColumnHeaderRecipe width='16%'>{strings.Cylindrical}</TableColumnHeaderRecipe>
                                <TableColumnHeaderRecipe width='16%'>{strings.Axis}</TableColumnHeaderRecipe>
                                <TableColumnHeaderRecipe width='16%'>{strings.DNP}</TableColumnHeaderRecipe>
                                <TableColumnHeaderRecipe width='70px'>{strings.Height}</TableColumnHeaderRecipe>
                            </TableRowRecipe>
                        </TableHeaderRecipe>
                        <TableBodyRecipe>
                            <TableRowRecipe>
                                <TableColumnBodyRecipe>{strings.Far}</TableColumnBodyRecipe>
                                <TableColumnBodyRecipe>{strings.Right}</TableColumnBodyRecipe>
                                <TableColumnBodyRecipe>{farDiagnostic?.rightSpherical}</TableColumnBodyRecipe>
                                <TableColumnBodyRecipe>{farDiagnostic?.rightCylindrical}</TableColumnBodyRecipe>
                                <TableColumnBodyRecipe>{farDiagnostic?.rightAxis}</TableColumnBodyRecipe>
                                <TableColumnBodyRecipe>{farDiagnostic?.rightNasoPupillaryDistance}</TableColumnBodyRecipe>
                                <TableColumnBodyRecipe>{recipe?.rightEyeHeight}</TableColumnBodyRecipe>
                            </TableRowRecipe>
                            <TableRowRecipe>
                                <TableColumnBodyRecipe>{strings.Far}</TableColumnBodyRecipe>
                                <TableColumnBodyRecipe>{strings.Left}</TableColumnBodyRecipe>
                                <TableColumnBodyRecipe>{farDiagnostic?.leftSpherical}</TableColumnBodyRecipe>
                                <TableColumnBodyRecipe>{farDiagnostic?.leftCylindrical}</TableColumnBodyRecipe>
                                <TableColumnBodyRecipe>{farDiagnostic?.leftAxis}</TableColumnBodyRecipe>
                                <TableColumnBodyRecipe>{farDiagnostic?.leftNasoPupillaryDistance}</TableColumnBodyRecipe>
                                <TableColumnBodyRecipe>{recipe?.leftEyeHeight}</TableColumnBodyRecipe>
                            </TableRowRecipe>
                            <TableRowRecipe>
                                <TableColumnBodyRecipe>{strings.Near}</TableColumnBodyRecipe>
                                <TableColumnBodyRecipe>{strings.Right}</TableColumnBodyRecipe>
                                <TableColumnBodyRecipe>{nearDiagnostic?.rightSpherical}</TableColumnBodyRecipe>
                                <TableColumnBodyRecipe>{nearDiagnostic?.rightCylindrical}</TableColumnBodyRecipe>
                                <TableColumnBodyRecipe>{nearDiagnostic?.rightAxis}</TableColumnBodyRecipe>
                                <TableColumnBodyRecipe>{nearDiagnostic?.rightNasoPupillaryDistance}</TableColumnBodyRecipe>
                                <TableColumnBodyRecipe>{recipe?.rightEyeHeight}</TableColumnBodyRecipe>
                            </TableRowRecipe>
                            <TableRowRecipe>
                                <TableColumnBodyRecipe>{strings.Near}</TableColumnBodyRecipe>
                                <TableColumnBodyRecipe>{strings.Left}</TableColumnBodyRecipe>
                                <TableColumnBodyRecipe>{nearDiagnostic?.leftSpherical}</TableColumnBodyRecipe>
                                <TableColumnBodyRecipe>{nearDiagnostic?.leftCylindrical}</TableColumnBodyRecipe>
                                <TableColumnBodyRecipe>{nearDiagnostic?.leftAxis}</TableColumnBodyRecipe>
                                <TableColumnBodyRecipe>{nearDiagnostic?.leftNasoPupillaryDistance}</TableColumnBodyRecipe>
                                <TableColumnBodyRecipe>{recipe?.leftEyeHeight}</TableColumnBodyRecipe>
                            </TableRowRecipe>
                        </TableBodyRecipe>
                    </Table>
                </CardTabulatedData>

                {multifocalInfo}

                <CardSimpleData>
                    <CardData>{originRecipeCreation}</CardData>
                    <CardData>{originRecipeChange}</CardData>
                </CardSimpleData>

                <CardApprovalData>
                    <CardData>{strings.Recipeconfirmed}: {recipe.recipeApproved ? strings.Yes : strings.Not}</CardData>
                    <CardData>{strings.DNPsconfirmed}: {recipe.nasoPupillaryDistanceApproved ? strings.Yes : strings.Not}</CardData>
                </CardApprovalData>
            </CardContent>
            {buttons}
        </CardContainer>
    );
}